import React, { useState, useMemo, useCallback } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getSortedRowModel,
  ColumnFiltersState,
  SortingState,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Search } from 'lucide-react';
import { type ItemAvailabilityMatrix } from '@/app/services/itemAvailabilityService';
import { getColumns } from './columns';

interface ItemSiteAvailabilityTableProps {
  matrix: ItemAvailabilityMatrix;
  modifiedCells: Map<string, boolean>;
  selectedItems: Set<number>;
  selectedSites: Set<number>;
  onAvailabilityChange: (itemId: number, siteId: number, isAvailable: boolean) => void;
  onItemSelectionChange: (items: Set<number>) => void;
  onSiteSelectionChange: (sites: Set<number>) => void;
}

export default function ItemSiteAvailabilityTable({
  matrix,
  modifiedCells,
  selectedItems,
  selectedSites,
  onAvailabilityChange,
  onItemSelectionChange,
  onSiteSelectionChange,
}: ItemSiteAvailabilityTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  // Transform matrix data into table rows
  const tableData = useMemo(() => {
    return matrix.items.map((item, itemIndex) => {
      const row: any = {
        id: item.id,
        name: item.name,
        code: item.code,
        category_id: item.category_id,
        category_name: item.category_name,
        _itemIndex: itemIndex,
      };

      // Add site availability as columns
      matrix.sites.forEach((site, siteIndex) => {
        const key = `${item.id}-${site.id}`;
        const isModified = modifiedCells.has(key);
        const currentValue = isModified
          ? modifiedCells.get(key)!
          : matrix.availability[itemIndex][siteIndex];

        row[`site_${site.id}`] = {
          value: currentValue,
          isModified,
          itemId: item.id,
          siteId: site.id,
        };
      });

      return row;
    });
  }, [matrix, modifiedCells]);

  // Generate columns dynamically
  const columns = useMemo(
    () => getColumns(matrix.sites, onAvailabilityChange, selectedSites, onSiteSelectionChange),
    [matrix.sites, onAvailabilityChange, selectedSites, onSiteSelectionChange]
  );

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Handle item selection
  const handleItemSelection = useCallback((itemId: number, checked: boolean) => {
    const newSelection = new Set(selectedItems);
    if (checked) {
      newSelection.add(itemId);
    } else {
      newSelection.delete(itemId);
    }
    onItemSelectionChange(newSelection);
  }, [selectedItems, onItemSelectionChange]);

  // Handle select all items
  const handleSelectAllItems = useCallback((checked: boolean) => {
    if (checked) {
      const allItemIds = new Set(tableData.map(row => row.id));
      onItemSelectionChange(allItemIds);
    } else {
      onItemSelectionChange(new Set());
    }
  }, [tableData, onItemSelectionChange]);

  const allItemsSelected = selectedItems.size === tableData.length && tableData.length > 0;
  const someItemsSelected = selectedItems.size > 0 && selectedItems.size < tableData.length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search items by name or code..."
            value={globalFilter ?? ''}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="pl-9 text-sm"
          />
        </div>
        <div className="flex items-center gap-4 text-xs sm:text-sm text-muted-foreground">
          <span>
            {selectedItems.size} items selected
          </span>
          <span>
            {selectedSites.size} sites selected
          </span>
          {modifiedCells.size > 0 && (
            <span className="text-warning font-medium">
              {modifiedCells.size} changes
            </span>
          )}
        </div>
      </div>

      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={
                    allItemsSelected ||
                    (someItemsSelected && "indeterminate")
                  }
                  onCheckedChange={handleSelectAllItems}
                  aria-label="Select all items"
                />
              </TableHead>
              {table.getHeaderGroups()[0].headers.map((header) => (
                <TableHead 
                  key={header.id}
                  className={header.column.id.startsWith('site_') ? 'text-center min-w-[100px]' : ''}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={selectedItems.has(row.original.id) ? "bg-muted/50" : ""}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedItems.has(row.original.id)}
                      onCheckedChange={(checked) => 
                        handleItemSelection(row.original.id, checked as boolean)
                      }
                      aria-label={`Select ${row.original.name}`}
                    />
                  </TableCell>
                  {row.getVisibleCells().map((cell) => {
                    const isSiteColumn = cell.column.id.startsWith('site_');
                    const cellData = isSiteColumn ? cell.getValue() as any : null;
                    const isModified = cellData?.isModified;
                    
                    return (
                      <TableCell 
                        key={cell.id}
                        className={`
                          ${isSiteColumn ? 'text-center p-2' : ''}
                          ${isModified ? 'bg-warning/5' : ''}
                        `}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  className="h-24 text-center"
                >
                  No items found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}